// Framing rule check from MediaPipe FaceLandmarker output, evaluated in AREA-2
// viewport space. Thresholds owned by docs/gameplay-config.md#capture
// (config/capture.ts).
//
// MediaPipe face landmarks come back as 478 normalized (0..1) points in the FULL
// video source frame. We derive a bbox from min/max landmark X,Y, project its
// corners into area-2-relative fractions (capture/cover-map.ts), then apply the
// rules against the area-2 viewfinder — the same region the capture crop uses and
// the alignment-guide oval is drawn in. Gate, guide, and crop share one geometry.
//
// Tilt is the exception: it is measured on RAW landmarks because the cover map's
// scale is isotropic and so preserves angles (see cover-map.ts). Do not project
// the eye-corner points.

import type { NormalizedLandmark } from "@mediapipe/tasks-vision";
import {
  FRAMING_BOTTOM_MARGIN,
  FRAMING_CROWN_PAD,
  FRAMING_FACE_CENTER_TOLERANCE,
  FRAMING_FACE_MAX_FRACTION,
  FRAMING_FACE_MIN_FRACTION,
  FRAMING_MAX_PITCH_DEGREES,
  FRAMING_MAX_TILT_DEGREES,
  FRAMING_MAX_YAW_DEGREES,
  FRAMING_REQUIRE_HEAD_TOP,
  FRAMING_TOP_MARGIN,
} from "../config/capture";
import type { CoverMap } from "./cover-map";
import { sourceNormToArea2Frac } from "./cover-map";
import type { DisplayRect } from "./capture-image";
import type { HeadPose } from "./head-pose";

// MediaPipe FaceMesh eye-outer-corner indices, used for head-tilt detection.
const EYE_OUTER_RIGHT = 33;   // subject's right eye outer corner
const EYE_OUTER_LEFT = 263;   // subject's left eye outer corner

export type FramingReason =
  | "no_face"
  | "off_center"
  | "too_small"
  | "too_close"
  | "head_clipped"
  | "chin_clipped"
  | "tilted"
  | "looking_away";

export type FramingResult = { ok: true } | { ok: false; reason: FramingReason };

// Evaluate framing inside the area-2 viewfinder. `map`/`a2RectRel` are null until
// the camera and layout are ready; treat that as "no face" (searching).
export function checkFramingArea2(
  landmarks: NormalizedLandmark[] | undefined,
  map: CoverMap | null,
  a2RectRel: DisplayRect | null,
  headPose: HeadPose | null,
): FramingResult {
  if (!landmarks || landmarks.length === 0) return { ok: false, reason: "no_face" };
  if (!map || !a2RectRel) return { ok: false, reason: "no_face" };

  // Source-normalized bbox.
  let minX = 1, minY = 1, maxX = 0, maxY = 0;
  for (const p of landmarks) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }

  // Project the bbox corners into area-2 fractions. The cover map is affine and
  // order-preserving, so source min/max map to area-2 min/max.
  const tl = sourceNormToArea2Frac(map, a2RectRel, minX, minY);
  const br = sourceNormToArea2Frac(map, a2RectRel, maxX, maxY);

  const centerX = (tl.x + br.x) / 2;
  const centerY = (tl.y + br.y) / 2;
  if (
    Math.abs(centerX - 0.5) > FRAMING_FACE_CENTER_TOLERANCE ||
    Math.abs(centerY - 0.5) > FRAMING_FACE_CENTER_TOLERANCE
  ) {
    return { ok: false, reason: "off_center" };
  }

  // Face width as a fraction of area-2 width (mirror-invariant, stable basis).
  const faceWidth = br.x - tl.x;
  if (faceWidth < FRAMING_FACE_MIN_FRACTION) {
    return { ok: false, reason: "too_small" };
  }
  // "too_close" is checked before "head_clipped": an oversized face also trips
  // the head-top rule, and "lùi ra xa" is the more actionable message.
  if (faceWidth > FRAMING_FACE_MAX_FRACTION) {
    return { ok: false, reason: "too_close" };
  }

  // Vertical fit: the whole head (forehead bbox padded up by an estimated crown,
  // through to the chin) must sit inside area-2. The bbox top is the hairline, so
  // we pad upward by FRAMING_CROWN_PAD × bbox height to approximate the real crown.
  const faceH = br.y - tl.y;
  const headTop = tl.y - FRAMING_CROWN_PAD * faceH;
  if (FRAMING_REQUIRE_HEAD_TOP && headTop < FRAMING_TOP_MARGIN) {
    return { ok: false, reason: "head_clipped" };
  }
  if (br.y > 1 - FRAMING_BOTTOM_MARGIN) {
    return { ok: false, reason: "chin_clipped" };
  }

  // Tilt on raw landmarks — isotropic cover scale preserves the angle.
  const eyeRight = landmarks[EYE_OUTER_RIGHT];
  const eyeLeft = landmarks[EYE_OUTER_LEFT];
  if (eyeRight && eyeLeft) {
    const dx = eyeLeft.x - eyeRight.x;
    const dy = eyeLeft.y - eyeRight.y;
    const tiltDeg = (Math.atan2(dy, dx) * 180) / Math.PI;
    if (Math.abs(tiltDeg) > FRAMING_MAX_TILT_DEGREES) {
      return { ok: false, reason: "tilted" };
    }
  }

  // "Look at the camera": reject heads turned (yaw) or nodded (pitch) off-axis.
  // Magnitude-only, so a flipped axis sign in the matrix decomposition is safe.
  if (headPose) {
    if (
      Math.abs(headPose.yaw) > FRAMING_MAX_YAW_DEGREES ||
      Math.abs(headPose.pitch) > FRAMING_MAX_PITCH_DEGREES
    ) {
      return { ok: false, reason: "looking_away" };
    }
  }

  return { ok: true };
}

export const FRAMING_REASON_VI: Record<FramingReason, string> = {
  no_face: "Không thấy khuôn mặt — hãy đưa mặt vào khung",
  off_center: "Hãy đưa mặt vào giữa khung",
  too_small: "Tiến lại gần camera hơn",
  too_close: "Lùi ra xa hơn một chút",
  head_clipped: "Lùi ra một chút — đầu bị che",
  chin_clipped: "Lùi ra một chút — cằm bị che",
  tilted: "Giữ đầu thẳng",
  looking_away: "Hãy nhìn thẳng vào camera",
};
